import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type { Job, JobWithDetails, JobFilters, JobInsert } from '../../../features/jobs/types/job'
import { jobService } from '../../../features/jobs/services/jobService'
import { logout } from './authSlice'

interface JobsState {
  items: JobWithDetails[]
  currentJob: JobWithDetails | null
  loading: boolean
  error: string | null
  filters: JobFilters
}

const initialState: JobsState = {
  items: [],
  currentJob: null,
  loading: false,
  error: null,
  filters: {},
}

// Async thunks
export const fetchJobs = createAsyncThunk('jobs/fetchJobs', async (filters?: JobFilters) => {
  return await jobService.getJobs(filters)
})

export const fetchJobById = createAsyncThunk('jobs/fetchJobById', async (id: string) => {
  return await jobService.getJob(id)
})

export const createJob = createAsyncThunk('jobs/createJob', async (jobData: Partial<Job>) => {
  return await jobService.createJob(jobData as JobInsert)
})

export const updateJob = createAsyncThunk(
  'jobs/updateJob',
  async ({ id, updates }: { id: string; updates: Partial<Job> }) => {
    return await jobService.updateJob(id, updates)
  }
)

export const deleteJob = createAsyncThunk('jobs/deleteJob', async (id: string) => {
  await jobService.deleteJob(id)
  return id
})

export const completeJob = createAsyncThunk('jobs/completeJob', async (id: string) => {
  return await jobService.completeJob(id)
})

// Slice
const jobsSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<JobFilters>) => {
      state.filters = action.payload
    },
    clearCurrentJob: (state) => {
      state.currentJob = null
    },
    clearError: (state) => {
      state.error = null
    },
    clearJobs: (state) => {
      state.items = []
      state.currentJob = null
      state.error = null
      state.filters = {}
    },
  },
  extraReducers: (builder) => {
    // Fetch jobs
    builder
      .addCase(fetchJobs.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch jobs'
      })

    // Fetch job by ID
    builder
      .addCase(fetchJobById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchJobById.fulfilled, (state, action) => {
        state.loading = false
        state.currentJob = action.payload
      })
      .addCase(fetchJobById.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch job'
      })

    // Create job
    builder
      .addCase(createJob.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createJob.fulfilled, (state, action) => {
        state.loading = false
        state.items.unshift(action.payload as JobWithDetails)
      })
      .addCase(createJob.rejected, (state) => {
        state.loading = false
        // Don't set global error - let the form handle validation errors locally
      })

    // Update job
    builder
      .addCase(updateJob.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateJob.fulfilled, (state, action) => {
        state.loading = false
        const index = state.items.findIndex((job) => job.id === action.payload.id)
        if (index !== -1) {
          state.items[index] = action.payload as JobWithDetails
        }
        if (state.currentJob?.id === action.payload.id) {
          state.currentJob = action.payload as JobWithDetails
        }
      })
      .addCase(updateJob.rejected, (state) => {
        state.loading = false
        // Don't set global error - let the form handle validation errors locally
      })

    // Delete job
    builder
      .addCase(deleteJob.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteJob.fulfilled, (state, action) => {
        state.loading = false
        state.items = state.items.filter((job) => job.id !== action.payload)
        if (state.currentJob?.id === action.payload) {
          state.currentJob = null
        }
      })
      .addCase(deleteJob.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete job'
      })

    // Complete job
    builder
      .addCase(completeJob.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(completeJob.fulfilled, (state, action) => {
        state.loading = false
        const index = state.items.findIndex((job) => job.id === action.payload.id)
        if (index !== -1) {
          state.items[index] = action.payload as JobWithDetails
        }
        if (state.currentJob?.id === action.payload.id) {
          state.currentJob = action.payload as JobWithDetails
        }
      })
      .addCase(completeJob.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to complete job'
      })

    // Clear jobs state on logout
    builder.addCase(logout.fulfilled, (state) => {
      state.items = []
      state.currentJob = null
      state.loading = false
      state.error = null
      state.filters = {}
    })
  },
})

export const { setFilters, clearCurrentJob, clearError, clearJobs } = jobsSlice.actions
export const jobsReducer = jobsSlice.reducer
